import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowDown, GraduationCap, Landmark, Compass, Award, ShieldCheck,
  HeartHandshake, Search, Lightbulb, TrendingUp, Quote, Phone, Mail, MapPin, CalendarCheck,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BOOKING_URL } from "@/config";
import { useSeo } from "@/lib/seo";

const TIMELINE = [
  { icon: GraduationCap, place: "Cameroun", text: "Formation en économie et finance." },
  { icon: Landmark, place: "Microfinance", text: "Analyse de crédit • Gestion de clientèle • Accompagnement financier." },
  { icon: Compass, place: "Nouveau départ au Québec", text: "Découverte d'un nouvel environnement économique et financier." },
  { icon: Award, place: "Certification AMF", text: "Sécurité financière." },
  { icon: ShieldCheck, place: "Conseiller en sécurité financière", text: "iA Groupe financier." },
  { icon: HeartHandshake, place: "Mon Projet Protégé", text: "Une démarche pour mieux comprendre, structurer et protéger ce que l'on construit.", highlight: true },
];

const VALUES = [
  { icon: Search, title: "Comprendre avant de proposer", text: "Votre situation, vos responsabilités, vos projets et vos priorités passent avant les produits." },
  { icon: Lightbulb, title: "Expliquer avant de décider", text: "Assurance vie, invalidité, maladies graves, REEE, CELI, CELIAPP, REER et autres solutions peuvent parfois sembler complexes. Mon rôle est aussi de les rendre compréhensibles afin que vous puissiez prendre des décisions éclairées." },
  { icon: TrendingUp, title: "Construire dans le temps", text: "Votre situation évolue : nouvel emploi, arrivée au Québec, naissance d'un enfant, achat d'une propriété, entrepreneuriat, croissance des revenus ou préparation de la retraite. Votre stratégie financière doit pouvoir évoluer avec vous." },
];

const PROJETS = ["Une famille", "Une carrière", "Une entreprise", "Une première propriété", "Les études de vos enfants", "Votre patrimoine", "Votre retraite"];

export default function MonParcours() {
  useSeo("Mon parcours — Romain Toussé Mélataguia, conseiller en sécurité financière",
    "Du secteur financier au Cameroun à la sécurité financière au Québec : le parcours de Romain Toussé Mélataguia et sa façon d'accompagner les familles qui construisent leur avenir.",
    {
      image: typeof window !== "undefined" ? `${window.location.origin}/og-parcours.jpg` : undefined,
      url: typeof window !== "undefined" ? `${window.location.origin}/mon-parcours` : undefined,
      ogTitle: "Je connais la valeur d'un nouveau départ.",
      ogDescription: "Avant de parler de produits financiers, je cherche à comprendre ce que vous êtes en train de construire. Découvrez mon parcours.",
    });

  return (
    <div data-testid="mon-parcours-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full blob" style={{ background: "#012141" }} />
        <div className="absolute top-40 -right-20 w-80 h-80 rounded-full blob" style={{ background: "#C89A3E" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-12 sm:pt-20 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <Reveal className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#012141]/10 shadow-sm text-sm font-semibold text-[#012141]">
                <MapPin size={16} style={{ color: "#C89A3E" }} /> Lévis, Québec
              </Reveal>
              <Reveal as="h1" delay={80} className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#012141] leading-[1.06] font-heading">
                Je connais la valeur d'un <span style={{ color: "#C89A3E" }}>nouveau départ.</span>
              </Reveal>
              <Reveal as="p" delay={160} className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
                De la finance au Cameroun à la sécurité financière au Québec, mon parcours m'a appris qu'avant de parler de produits financiers, il faut comprendre ce qu'une personne essaie réellement de construire.
              </Reveal>
              <Reveal delay={220} className="mt-7 border-l-2 border-[#C89A3E] pl-4">
                <div className="font-heading font-bold text-[#012141] text-lg">Romain Toussé Mélataguia</div>
                <div className="text-sm text-slate-600">Conseiller en sécurité financière</div>
                <div className="text-sm font-semibold text-[#012141]">iA Groupe financier</div>
              </Reveal>
              <Reveal delay={300} className="mt-8">
                <Link to="/diagnostic" data-testid="parcours-hero-diagnostic-btn"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#012141] text-white font-semibold shadow-xl hover:bg-[#00172E] hover:-translate-y-0.5 transition-all">
                  Faire le point sur mon projet <ArrowRight size={18} />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={200} className="relative">
                <div className="rounded-3xl overflow-hidden border border-[#012141]/10 shadow-[0_30px_60px_-25px_rgba(18,49,92,0.45)]">
                  <img src="/romain.jpg" alt="Romain Toussé Mélataguia, conseiller en sécurité financière" className="w-full h-[420px] sm:h-[520px] object-cover object-top" />
                </div>
                <div className="absolute -bottom-5 left-5 bg-white rounded-2xl border border-[#012141]/10 shadow-xl px-5 py-3.5 flex items-center gap-3">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#012141] text-[#C89A3E]"><ShieldCheck size={20} /></span>
                  <div>
                    <div className="text-sm font-bold text-[#012141] font-heading">Certifié AMF</div>
                    <div className="text-xs text-slate-500">Sécurité financière</div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 1 — MON PARCOURS */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
        <Reveal>
          <span className="text-xs font-bold uppercase tracking-wider text-[#C89A3E]">Mon parcours</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#012141] font-heading leading-tight">Un parcours construit autour de la finance et de l'accompagnement</h2>
        </Reveal>
        <div className="mt-8 space-y-5 text-lg text-slate-600 leading-relaxed">
          <Reveal as="p" delay={60}>Je m'appelle <b className="text-[#012141]">Romain Toussé Mélataguia</b>.</Reveal>
          <Reveal as="p" delay={100}>Mon parcours professionnel a commencé au Cameroun, où j'ai travaillé pendant plusieurs années dans le domaine de la microfinance, de l'analyse de crédit et de l'accompagnement de clientèle.</Reveal>
          <Reveal as="p" delay={140}>Cette expérience m'a appris une chose essentielle : derrière un revenu, un financement, une assurance ou un projet d'investissement, il y a d'abord une personne, une famille et des projets qui comptent.</Reveal>
          <Reveal as="p" delay={180}>Mon arrivée au Québec m'a ensuite placé, à mon tour, devant une nouvelle réalité : comprendre un nouvel environnement financier, reconstruire ses repères, développer sa carrière et progressivement bâtir sa stabilité.</Reveal>
          <Reveal as="p" delay={220}>J'ai ainsi connu cette réalité sous deux angles : comme professionnel de la finance et comme personne ayant elle-même dû reconstruire ses repères économiques dans un nouvel environnement.</Reveal>
          <Reveal as="p" delay={260}>Aujourd'hui, je poursuis ce parcours au Québec comme conseiller en sécurité financière, titulaire d'un certificat de l'Autorité des marchés financiers (AMF), au sein d'iA Groupe financier.</Reveal>
        </div>
      </section>

      {/* SECTION 2 — FRISE */}
      <section className="bg-white border-y border-[#012141]/8">
        <div className="max-w-2xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
          <Reveal className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-[#C89A3E]">Étape par étape</span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#012141] font-heading">Le chemin parcouru</h2>
          </Reveal>
          <div className="relative">
            <div className="absolute left-[27px] top-4 bottom-4 w-px bg-[#012141]/12" aria-hidden />
            <div className="space-y-5">
              {TIMELINE.map((t, i) => (
                <Reveal key={t.place} delay={i * 70} className="relative flex items-start gap-5">
                  <span className="relative z-10 grid place-items-center w-14 h-14 shrink-0 rounded-2xl shadow-lg" style={{ backgroundColor: t.highlight ? "#C89A3E" : "#012141" }}>
                    <t.icon size={24} style={{ color: t.highlight ? "#012141" : "#F5C64E" }} />
                  </span>
                  <div className={`flex-1 rounded-2xl border p-5 shadow-sm ${t.highlight ? "bg-[#012141] border-[#012141]" : "bg-[#FDFDFD] border-[#012141]/10"}`}>
                    <div className={`text-xs font-bold uppercase tracking-wide ${t.highlight ? "text-[#F5C64E]" : "text-[#C89A3E]"}`}>{t.place}</div>
                    <p className={`mt-1.5 text-sm leading-relaxed ${t.highlight ? "text-white/85" : "text-slate-600"}`}>{t.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — MA CONVICTION */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-16 sm:py-24 text-center">
        <Reveal><Quote size={40} className="mx-auto text-[#C89A3E]" /></Reveal>
        <Reveal as="h2" delay={80} className="mt-6 text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-[#012141] font-heading leading-[1.15]">
          Une bonne conversation financière ne devrait pas commencer par un produit.
        </Reveal>
        <Reveal as="p" delay={160} className="mt-8 text-lg text-slate-600">Elle devrait commencer par une question :</Reveal>
        <Reveal as="p" delay={200} className="mt-2 text-2xl sm:text-3xl font-bold text-[#C89A3E] font-heading">Qu'est-ce que vous êtes en train de construire ?</Reveal>
        <Reveal delay={260} className="mt-8 flex flex-wrap justify-center gap-2.5">
          {PROJETS.map((p) => (
            <span key={p} className="px-4 py-2 rounded-full bg-white border border-[#012141]/10 text-sm font-semibold text-[#012141] shadow-sm">{p}</span>
          ))}
        </Reveal>
        <Reveal as="p" delay={320} className="mt-10 text-lg text-slate-600">Puis une deuxième question :</Reveal>
        <Reveal as="p" delay={360} className="mt-2 text-2xl sm:text-3xl font-bold text-[#012141] font-heading">Qu'est-ce qui pourrait fragiliser tout cela si rien n'était prévu ?</Reveal>
        <Reveal as="p" delay={420} className="mt-10 text-base text-slate-500">C'est à partir de ces deux questions que commence mon travail.</Reveal>
      </section>

      {/* SECTION 4 — PROPOSITION DE VALEUR */}
      <section className="bg-white border-y border-[#012141]/8">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
          <Reveal className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-wider text-[#C89A3E]">Ma proposition de valeur</span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#012141] font-heading">Clarifier. Protéger. Construire.</h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">Je vous aide à transformer vos projets de vie en priorités financières claires, puis à identifier les protections et les stratégies adaptées à votre situation.</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 80} className="rounded-3xl bg-[#FDFDFD] border border-[#012141]/8 p-7 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all">
                <span className="grid place-items-center w-14 h-14 rounded-2xl bg-[#012141] text-[#C89A3E] shadow-lg"><v.icon size={24} /></span>
                <h3 className="mt-5 text-lg font-bold text-[#012141] font-heading">{v.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — POURQUOI MON PROJET PROTÉGÉ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
        <Reveal>
          <span className="text-xs font-bold uppercase tracking-wider text-[#C89A3E]">La démarche</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#012141] font-heading leading-tight">Protéger un projet, pas simplement souscrire un produit.</h2>
        </Reveal>
        <div className="mt-8 space-y-5 text-lg text-slate-600 leading-relaxed">
          <Reveal as="p" delay={60}>Mon Projet Protégé est né d'un constat simple.</Reveal>
          <Reveal as="p" delay={100}>Lorsqu'on arrive au Québec, beaucoup d'efforts sont consacrés à l'installation : logement, emploi, intégration, crédit, famille et nouveaux projets.</Reveal>
          <Reveal delay={140} className="rounded-2xl bg-[#C89A3E]/10 border border-[#C89A3E]/25 px-6 py-5">
            <p className="text-xl font-bold text-[#012141] font-heading">Mais une question est parfois laissée de côté : comment protéger ce que l'on commence à construire ?</p>
          </Reveal>
          <Reveal as="p" delay={180}>J'ai donc voulu créer une démarche permettant de prendre du recul, de comprendre les outils disponibles au Québec et d'identifier progressivement les angles morts d'un projet familial et financier.</Reveal>
          <Reveal as="p" delay={220}>Cette approche possède une sensibilité particulière aux réalités des nouveaux arrivants et des familles qui construisent leur avenir au Québec, sans toutefois s'y limiter.</Reveal>
        </div>
      </section>

      {/* SECTION 6 — CE QUI COMPTE VRAIMENT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-4">
        <Reveal className="relative overflow-hidden rounded-3xl px-6 sm:px-14 py-14 sm:py-20" style={{ background: "#012141" }}>
          <div className="absolute -bottom-16 -left-10 w-72 h-72 rounded-full blob" style={{ background: "#C89A3E" }} />
          <div className="relative max-w-3xl">
            <div className="space-y-1.5 text-lg sm:text-xl text-white/55 font-heading">
              <p>Une assurance n'est qu'un outil.</p>
              <p>Un REEE n'est qu'un outil.</p>
              <p>Un CELIAPP n'est qu'un outil.</p>
              <p>Un REER n'est qu'un outil.</p>
            </div>
            <p className="mt-8 text-2xl sm:text-3xl font-bold text-white font-heading leading-snug">
              Ce qui compte réellement, c'est ce que ces outils vous permettent de <span style={{ color: "#F5C64E" }}>protéger</span> ou de <span style={{ color: "#F5C64E" }}>construire</span>.
            </p>
            <p className="mt-8 text-white/70 leading-relaxed">Mon rôle n'est pas de vous dire que vous avez besoin de tout. Il est de vous aider à distinguer :</p>
            <div className="mt-5 grid sm:grid-cols-3 gap-3">
              {["ce qui est important maintenant", "ce qui peut attendre", "ce qui mérite d'être anticipé"].map((x) => (
                <div key={x} className="rounded-2xl bg-white/10 border border-white/15 px-5 py-4 text-white font-semibold text-sm">{x}</div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* SECTION FINALE — CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-16 sm:py-24 text-center">
        <Reveal as="h2" className="text-3xl sm:text-4xl font-bold text-[#012141] font-heading">Vous avez fait beaucoup pour arriver jusqu'ici.</Reveal>
        <Reveal as="p" delay={80} className="mt-3 text-2xl sm:text-3xl font-bold text-[#C89A3E] font-heading">Maintenant, protégeons ce que vous construisez.</Reveal>
        <Reveal as="p" delay={140} className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
          Commencez par faire le point sur votre situation. En quelques minutes, identifiez ce qui est déjà solide et les éléments qui méritent peut-être davantage d'attention.
        </Reveal>
        <Reveal delay={200} className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/diagnostic" data-testid="parcours-final-diagnostic-btn"
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#012141] text-white font-semibold shadow-xl hover:bg-[#00172E] hover:-translate-y-0.5 transition-all">
            Faire le point sur mon projet <ArrowRight size={18} />
          </Link>
          <a href={BOOKING_URL} target="_blank" rel="noreferrer" data-testid="parcours-final-booking-btn"
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#C89A3E] text-[#012141] font-bold hover:brightness-95 hover:-translate-y-0.5 transition-all">
            <CalendarCheck size={18} /> Prendre rendez-vous
          </a>
        </Reveal>
        <Reveal delay={260} className="mt-12 inline-flex flex-col items-center gap-1.5 rounded-3xl border border-[#012141]/10 bg-white px-8 py-6 shadow-sm">
          <div className="font-heading font-bold text-[#012141]">Romain Toussé Mélataguia</div>
          <div className="text-sm text-slate-600">Conseiller en sécurité financière — iA Groupe financier</div>
          <div className="text-sm text-slate-500 flex items-center gap-1.5"><MapPin size={14} className="text-[#C89A3E]" /> Lévis, Québec</div>
          <div className="mt-2 flex flex-col sm:flex-row items-center gap-x-6 gap-y-1.5 text-sm font-semibold text-[#012141]">
            <a href="tel:+15814434803" className="inline-flex items-center gap-1.5 hover:text-[#C89A3E] transition-colors"><Phone size={15} className="text-[#C89A3E]" /> 581 443 4803</a>
            <a href="mailto:romain.tousse@agc.ia.ca" className="inline-flex items-center gap-1.5 hover:text-[#C89A3E] transition-colors"><Mail size={15} className="text-[#C89A3E]" /> romain.tousse@agc.ia.ca</a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
